import { useMemo, useSyncExternalStore } from "react"
import { PANEL_VIEWPORT_GAP, CLOSED_DRAWER_OFFSET, appBarHeight, drawerWidth } from "@/config/layout"

// Shared frame the docked panels lay out against. Each measure has exactly
// one publisher (window, time slider, drawer, left panel); readers derive edges.
const INITIAL_STATE = { viewportWidth: 0, viewportHeight: 0, sliderHeight: 0, drawerOpen: false, leftColumnRight: null }
let state = INITIAL_STATE
const listeners = new Set()
let windowBound = false

function emit(patch) {
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
}

function readViewport() {
  return { viewportWidth: window.innerWidth, viewportHeight: window.innerHeight }
}

function bindWindow() {
  if (windowBound || typeof window === "undefined") return
  windowBound = true
  state = { ...state, ...readViewport() }
  window.addEventListener("resize", () => emit(readViewport()))
}

function subscribe(listener) {
  bindWindow()
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  bindWindow()
  return state
}

export function publishSliderHeight(px) {
  const next = Math.max(0, Number(px) || 0)
  if (state.sliderHeight !== next) emit({ sliderHeight: next })
}

export function publishDrawerOpen(open) {
  const next = Boolean(open)
  if (state.drawerOpen !== next) emit({ drawerOpen: next })
}

// Right edge of the open left-column panel (null when none is open).
export function publishLeftColumnRight(px) {
  const next = Number.isFinite(px) ? px : null
  if (state.leftColumnRight !== next) emit({ leftColumnRight: next })
}

export function layoutBoundsFrom(snapshot) {
  const top = appBarHeight + PANEL_VIEWPORT_GAP
  const bottom = snapshot.viewportHeight - snapshot.sliderHeight - PANEL_VIEWPORT_GAP
  return {
    top,
    bottom,
    left: snapshot.drawerOpen ? drawerWidth + PANEL_VIEWPORT_GAP : CLOSED_DRAWER_OFFSET,
    right: snapshot.viewportWidth - PANEL_VIEWPORT_GAP,
    panelHeight: Math.max(0, bottom - top),
    leftColumnRight: snapshot.leftColumnRight,
    sliderHeight: snapshot.sliderHeight,
    viewportWidth: snapshot.viewportWidth,
    viewportHeight: snapshot.viewportHeight,
  }
}

export function useLayoutBounds() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return useMemo(() => layoutBoundsFrom(snapshot), [snapshot])
}

// Test hook: reset the store between cases.
export function resetLayoutBoundsForTests(overrides = {}) {
  state = INITIAL_STATE
  emit(overrides)
}
